import React from 'react'
import {
  TextField,
  FormControl,
  FormHelperText,
  Tooltip,
  Icon,
  Typography,
  MenuItem,
} from '@mui/material'
import { withStyles } from '@mui/styles'

import { textInputStyles } from './text-input.styles'

const ROWS_MAX = 5

/**
 * @typedef {object} TextOption
 * @property {string | number} key - El valor único de la opción.
 * @property {string} value - El texto que se muestra para la opción.
 */

/**
 * @typedef {object} TextInputProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por `withStyles`.
 * @property {'text' | 'email' | 'password' | 'number' | 'tel' | 'url'} [type='text'] - El tipo de input HTML.
 * @property {boolean} [readOnly=false] - Si es `true`, muestra el valor como texto de solo lectura.
 * @property {number} [rowsMax=5] - Número máximo de filas para inputs multilinea.
 * @property {string} [title] - Título que se muestra encima del input.
 * @property {string} [tooltip] - Texto del tooltip de ayuda.
 * @property {string} [value] - El valor actual del input.
 * @property {string} [placeholder] - Texto de marcador de posición.
 * @property {function(object): void} onChange - Callback que se ejecuta cuando el valor cambia.
 * @property {boolean} [error] - Si es `true`, muestra el input en estado de error.
 * @property {string} [errorMessage] - Mensaje de error que se muestra cuando `error` es `true`.
 * @property {string} [helpMessage] - Mensaje de ayuda que se muestra cuando no hay error.
 * @property {string} [units] - Unidades que se muestran al final del valor en modo solo lectura.
 * @property {TextOption[]} [values] - Array de opciones para crear un select nativo.
 * @property {'small' | 'medium'} [fontSize] - Tamaño de la fuente para el título.
 */

/**
 * Componente de entrada de texto versátil con soporte para múltiples tipos y validación.
 * 
 * Proporciona un input de texto completo con:
 * - **Múltiples tipos**: text, email, password, number, tel, url
 * - **Modo solo lectura**: Visualización formateada del valor
 * - **Validación visual**: Estados de error con mensajes personalizados
 * - **Tooltip de ayuda**: Información contextual opcional
 * - **Select nativo**: Soporte para opciones predefinidas
 * - **Multilinea**: Inputs de texto largo con límite de filas
 * 
 * @component
 * @example
 * const MyForm = () => {
 *   const [name, setName] = useState('');
 *   const [error, setError] = useState(false);
 * 
 *   return (
 *     <TextInput
 *       name="name"
 *       title="Nombre Completo"
 *       value={name}
 *       onChange={(e) => setName(e.target.value)}
 *       placeholder="Ingresa tu nombre"
 *       error={error}
 *       errorMessage="El nombre es requerido"
 *       tooltip="Mínimo 3 caracteres"
 *     />
 *   );
 * };
 * 
 * @param {TextInputProps} props - Las props del componente TextInput.
 */
export const TextInput = React.memo(
  withStyles(textInputStyles)(
    ({ classes, type = 'text', readOnly = false, rowsMax = ROWS_MAX, ...props }) => {
      const InputProps = { className: classes.input, ...props.InputProps }
      const tooltip = (
        <span style={{ fontSize: 16, marginLeft: 4 }}>
          <Tooltip
            placement="top"
            title={
              <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
                {props.tooltip}
              </Typography>
            }
          >
            <Icon fontSize="inherit">help_outline</Icon>
          </Tooltip>
        </span>
      )

      const typographyVariant = props.fontSize === 'small' ? '2' : '1'
      return (
        <FormControl className={classes.root}>
          {props.title ? (
            <Typography variant={`subtitle${typographyVariant}`} className={classes.title}>
              {props.title + (props.tooltip ? tooltip : '')}
            </Typography>
          ) : null}

          {readOnly ? (
            <Typography
              variant={`body${typographyVariant}`}
              color="text.secondary"
              style={{ whiteSpace: 'pre-line' }}
            >
              {props.value ? props.value + (props.units ? ' ' + props.units : '') : '--'}
            </Typography>
          ) : (
            <>
              <TextField
                maxRows={rowsMax}
                {...props}
                type={type}
                variant="outlined"
                value={props.value || ''}
                placeholder={props.placeholder}
                onChange={props.onChange}
                InputProps={InputProps}
                // autoComplete="off"
                // inputProps={{ maxLength: props.maxLength }}
                // InputProps={{ className: classes.input, ...props.InputProps }}
              >
                {props.values
                  ? props.values.map((option) => (
                      <MenuItem key={option.key} value={option.key}>
                        {option.value}
                      </MenuItem>
                    ))
                  : null}
              </TextField>
              <FormHelperText id="my-helper-text" error={props.error}>
                {props.error ? props.errorMessage : props.helpMessage}
              </FormHelperText>
            </>
          )}
        </FormControl>
      )
    }
  )
)
